import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import "../../../assets/css/setting.css";
import { AppContext } from "../../../context/AppContext";
import type { WorkflowEnumItem } from "../hooks/useWorkflowEnums";
import type { ActionNodeData } from "../nodes/ActionNode";
import { useWorkflowStore } from "../store";
import { Trigger } from "../nodes/TriggerNode";
import { selectSelectedStep, selectSelectedStepIndex } from "../store/selectors";
import { getActionIconAndClass, getFormTypeForAction } from "../utils/actionCodeMapping";
import { ACTION_HELP_CONTENT } from "../utils/helpContentConfig";
import { ConfigPanelFooter } from "./ConfigPanelFooter";
import { ConfigPanelShell } from "./ConfigPanelShell";
import { StepTypePicker } from "./StepTypePicker";
import { useStepConfigPanel } from "../hooks/useStepConfigPanel";
import { getActionDraftResetForTypePicker } from "./actionDraftPickerReset";
import { NOTIFY_USER_ACTION_CODE } from "../utils/notifyUserStaticMessage";
import { normalizeStepConfig } from "../utils/normalizeStepConfig";
import { StepFormResolver } from "./StepFormResolver";
import { readWorkflowCrmHelpOverrides, resolveCrmActionHelpContent } from "../utils/workflowCrmActionOverrides";
import { getActionStrategy, isNotifyUserActionDataInvalid } from "../parser/actionStrategies";

export interface ActionConfigPanelProps {
    actionTypes: WorkflowEnumItem[];
    isNarrowLayout: boolean;
}

export const ActionConfigPanel: React.FC<ActionConfigPanelProps> = ({ actionTypes, isNarrowLayout }) => {
    const [appProperties] = useContext(AppContext);
    const selectedStep = useWorkflowStore(selectSelectedStep);
    const selectedStepIndex = useWorkflowStore(selectSelectedStepIndex);
    const updateStepData = useWorkflowStore((s) => s.updateStepData);
    const deleteStep = useWorkflowStore((s) => s.deleteStep);
    const selectNode = useWorkflowStore((s) => s.selectNode);
    const incrementCloseSignal = useWorkflowStore((s) => s.incrementCloseSignal);
    const triggerType = useWorkflowStore((s) => s.triggerType);
    const committedApplyOncePerContact = useWorkflowStore((s) => s.applyOncePerContact);
    const setApplyOncePerContact = useWorkflowStore((s) => s.setApplyOncePerContact);
    const setPanelHasDraft = useWorkflowStore((s) => s.setPanelHasDraft);

    const nodeId = selectedStep?.id ?? "";
    const persistedData = useMemo((): ActionNodeData => {
        const raw = (selectedStep?.data as ActionNodeData) ?? {};
        const ft = getFormTypeForAction(raw.actionCode ?? raw.actionType ?? "");
        if (ft === "NotifyUser" && raw.message !== undefined) {
            const rest = { ...raw };
            delete rest.message;
            return rest;
        }
        return raw;
    }, [selectedStep?.data]);

    const transformActionDraft = useCallback((prev: ActionNodeData, partial: Partial<ActionNodeData>) => {
        const next = { ...prev, ...partial };
        const ft = getFormTypeForAction(next.actionCode ?? next.actionType ?? "");
        if (ft === "NotifyUser") {
            const rest = { ...next };
            delete rest.message;
            return rest;
        }
        return next;
    }, []);

    const { draftData, updateDraft, commitDraft, hasDraftChanges } = useStepConfigPanel<ActionNodeData>(
        nodeId,
        persistedData,
        updateStepData,
        { transformDraft: transformActionDraft }
    );

    const [draftApplyOncePerContact, setDraftApplyOncePerContact] = useState<boolean>(
        committedApplyOncePerContact ?? false
    );
    useEffect(() => {
        setDraftApplyOncePerContact(committedApplyOncePerContact ?? false);
    }, [nodeId, committedApplyOncePerContact]);


    const isPlaceholderStep =
        (persistedData.isPlaceholder ?? false) || (!persistedData.actionType && !persistedData.actionCode);
    const canCommit = Boolean(draftData.actionCode || draftData.actionType);
    const canAddStep = canCommit;
    const isAutomatedResponse = getFormTypeForAction(draftData.actionCode ?? draftData.actionType ?? "") === "AutomatedResponse";
    const isNotifyUserForm = getFormTypeForAction(draftData.actionCode ?? draftData.actionType ?? "") === "NotifyUser";
    const notifyUserDraftInvalid = isNotifyUserForm && isNotifyUserActionDataInvalid(draftData);
    const applyOnceDirty =
        isAutomatedResponse &&
        draftApplyOncePerContact !== (committedApplyOncePerContact ?? false);
    const canSaveChanges = hasDraftChanges || applyOnceDirty;

    useEffect(() => {
        setPanelHasDraft(canSaveChanges);
    }, [canSaveChanges, setPanelHasDraft]);

    const handleCloseDeselect = () => {
        if (isPlaceholderStep && selectedStep?.id) {
            deleteStep(selectedStep.id);
        }
        selectNode(null);
    };

    const handleBackToPicker = () => {
        updateDraft(getActionDraftResetForTypePicker());
    };
    const showPicker =
        !persistedData.actionType && !persistedData.actionCode && !draftData.actionType && !draftData.actionCode;

    const handleSelectActionType = (code: string, title: string) => {
        const formType = getFormTypeForAction(code);
        const initialPartial: Partial<ActionNodeData> = {
            actionCode: code,
            actionType: formType,
            title,
            isPlaceholder: true,
        };
        if (formType === "LogInCrm") {
            const selectedActionEnumItem = actionTypes.find((actionTypeItem) => actionTypeItem.code === code);
            const logInCrmFormFieldDefinitions = selectedActionEnumItem?.stepConfig?.formFields;
            if (logInCrmFormFieldDefinitions && logInCrmFormFieldDefinitions.length > 0) {
                for (const formFieldDefinition of logInCrmFormFieldDefinitions) {
                    if (formFieldDefinition.defaultValue !== undefined) {
                        (initialPartial as Record<string, unknown>)[formFieldDefinition.key] =
                            formFieldDefinition.defaultValue;
                    }
                }
            } else {
                initialPartial.logRecordsFor = "all";
                initialPartial.logRecordAsThread = "false";
                initialPartial.logRecordsOnStatus = "";
                initialPartial.activityType = "";
            }
        }
        if (formType === "Delay") {
            initialPartial.delayDays = 0;
            initialPartial.delayHours = 0;
            initialPartial.delayMinutes = 15;
        }
        if (formType === "CreateLead") {
            initialPartial.createLead = "true";
        }
        updateDraft(initialPartial);
    };

    const runActionValidation = useCallback((): boolean => {
        const code = draftData.actionCode;
        const strategy = getActionStrategy(code);
        if (!strategy) return true;
        const title = draftData.title ?? draftData.actionType ?? "Action";
        const errors = strategy.validate(draftData, nodeId, title);
        if (errors.length > 0) {
            message.warning(errors[0].message);
            return false;
        }
        return true;
    }, [draftData, nodeId]);

    const handlePrimaryClick = () => {
        if (isPlaceholderStep) {
            if (!canAddStep || selectedStepIndex < 0 || !selectedStep || !nodeId) return;
            if (!runActionValidation()) return;
            if (isAutomatedResponse) {
                setApplyOncePerContact(draftApplyOncePerContact);
            }
            commitDraft({ isPlaceholder: false });
            if (isNarrowLayout) {
                incrementCloseSignal();
                selectNode(null);
            }
            return;
        }
        if (!canSaveChanges) return;
        if (!runActionValidation()) return;
        if (isAutomatedResponse) {
            setApplyOncePerContact(draftApplyOncePerContact);
        }
        commitDraft();
        if (isNarrowLayout) {
            incrementCloseSignal();
            selectNode(null);
        }
    };

    const selectedActionEnum = useMemo(
        () => actionTypes.find((a) => a.code === draftData.actionCode),
        [actionTypes, draftData.actionCode]
    );

    const stepConfigForResolver = useMemo(() => {
        if (selectedActionEnum?.stepConfig) {
            return selectedActionEnum.stepConfig;
        }
        const code = draftData.actionCode ?? "";
        if (code === NOTIFY_USER_ACTION_CODE) {
            return normalizeStepConfig(
                selectedActionEnum ? (selectedActionEnum.meta as Record<string, unknown>) : null,
                NOTIFY_USER_ACTION_CODE
            );
        }
        return undefined;
    }, [selectedActionEnum, draftData.actionCode]);

    const formType = getFormTypeForAction(draftData.actionCode || draftData.actionType || "");
    const helpOverrides = useMemo(
        () => readWorkflowCrmHelpOverrides(appProperties?.controller),
        [appProperties?.controller]
    );
    const helpContent = useMemo(
        () =>
            resolveCrmActionHelpContent(draftData.actionCode, formType, helpOverrides, ACTION_HELP_CONTENT),
        [draftData.actionCode, formType, helpOverrides]
    );

    if (showPicker) {
        return (
            <ConfigPanelShell
                title="Actions"
                onClose={handleCloseDeselect}
                footer={<ConfigPanelFooter backOnly isPlaceholder={isPlaceholderStep} />}
            >
                <StepTypePicker
                    items={actionTypes}
                    onSelect={handleSelectActionType}
                    getIcon={(code) => getActionIconAndClass(code).icon}
                />
            </ConfigPanelShell>
        );
    }

    const displayTitle = draftData.title || draftData.actionType || draftData.actionCode;

    return (
        <ConfigPanelShell
            title={displayTitle ?? "Action"}
            onClose={isPlaceholderStep ? handleBackToPicker : handleCloseDeselect}
            helpContent={helpContent}
            footer={
                <ConfigPanelFooter
                    primaryLabel={isPlaceholderStep ? "Apply" : "Save Changes"}
                    primaryDisabled={
                        isPlaceholderStep
                            ? !(canAddStep || applyOnceDirty) || notifyUserDraftInvalid
                            : !canSaveChanges || notifyUserDraftInvalid
                    }
                    onPrimaryClick={handlePrimaryClick}
                    isPlaceholder={isPlaceholderStep}
                    hideDelete={triggerType === Trigger.Message.Outbound}
                    onBackToPicker={isPlaceholderStep ? handleBackToPicker : undefined}
                />
            }
        >
            <StepFormResolver
                actionCode={draftData.actionCode ?? ""}
                stepConfig={stepConfigForResolver}
                data={draftData}
                update={updateDraft}
                nodeId={nodeId}
                applyOnceDraft={draftApplyOncePerContact}
                onApplyOnceDraftChange={setDraftApplyOncePerContact}
            />
        </ConfigPanelShell>
    );
};
